import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DynamicServices } from 'src/app/features/ayen-services/services/dynamic-services/dynamic-services.service';

@Component({
  selector: 'app-completion-status',
  templateUrl: './completion-status.component.html',
  styleUrl: './completion-status.component.scss',
})
export class CompletionStatusComponent implements OnInit {
  completionStatusForm!: UntypedFormGroup;
  items!: FormArray;
  colorPalette: { id: number; color: string; color_name: string }[] = [];
  private colorNameByValue: Record<string, string> = {};
  private colorHexByName: Record<string, string> = {};
  private colorValueById: Record<string, string> = {};
  private colorNameById: Record<string, string> = {};

  get isEditMode(): boolean {
    const hasExplicitEditMode = this.data?.isEditMode === true;
    const hasId = this.data?.id !== null && this.data?.id !== undefined;
    return hasExplicitEditMode || hasId;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private _dialogRef: MatDialogRef<CompletionStatusComponent>,
    private dynamicService: DynamicServices
  ) {}

  ngOnInit() {
    this.completionStatusForm = new FormGroup({
      items: new FormArray([]),
    });

    this.loadColorPalette();
  }

  loadColorPalette() {
    this.dynamicService.getColorPalette().subscribe((data: any) => {
      const paletteSource = Array.isArray(data) && data.length > 0
        ? data
        : Array.isArray(data?.results) && data.results.length > 0
        ? data.results
        : [];

      this.colorPalette = paletteSource.map((item: any, index: number) => ({
        id: item?.id ?? index + 1,
        color: this.normalizeColorValue(item?.color ?? item?.hex ?? ''),
        color_name: this.resolveColorLabel(item),
      }));

      this.colorNameByValue = this.colorPalette.reduce((acc, item) => {
        if (item.color) acc[item.color] = item.color_name;
        return acc;
      }, {} as Record<string, string>);

      this.colorHexByName = this.colorPalette.reduce((acc, item) => {
        if (item.color_name) acc[item.color_name] = item.color;
        return acc;
      }, {} as Record<string, string>);

      this.colorValueById = this.colorPalette.reduce((acc, item) => {
        if (item.id !== null && item.id !== undefined) {
          acc[String(item.id)] = item.color;
        }
        return acc;
      }, {} as Record<string, string>);

      this.colorNameById = this.colorPalette.reduce((acc, item) => {
        if (item.id !== null && item.id !== undefined) {
          acc[String(item.id)] = item.color_name;
        }
        return acc;
      }, {} as Record<string, string>);

      if (this.isEditMode && this.data.choices?.length) {
        this.data.choices.forEach((choice: any) => this.addItem(choice));
      } else if (this.status().length === 0) {
        this.addItem();
      }

      this.syncItemColorsToPalette();
    }, () => {
      if (this.status().length === 0) {
        this.addItem();
      }
    });
  }

  createItem(choice?: any): FormGroup {
    return this.formBuilder.group({
      id: choice?.id ?? null,
      name: choice?.name ?? '',
      value: [choice?.value ?? '', [Validators.min(0), Validators.max(100)]],
      color: this.normalizeColorValue(choice?.color ?? ''),
      color_name: choice?.color_name ?? '',
    });
  }

  resolveColorLabel(item: any): string {
    return item?.color_name || item?.name || item?.label || item?.title || this.normalizeColorValue(item?.color ?? item?.hex ?? '') || 'اسم اللون';
  }

  hasPaletteColor(value: unknown): boolean {
    const idKey = value !== null && value !== undefined ? String(value).trim() : '';
    if (idKey && this.colorValueById[idKey]) {
      return true;
    }
    const normalized = this.normalizeColorValue(value);
    return Boolean(normalized && this.colorNameByValue[normalized]);
  }

  getColorDisplayName(value: unknown): string {
    const idKey = value !== null && value !== undefined ? String(value).trim() : '';
    if (idKey && this.colorNameById[idKey]) {
      return this.colorNameById[idKey];
    }

    const normalized = this.normalizeColorValue(value);
    return this.colorNameByValue[normalized] || normalized || 'اسم اللون';
  }

  private normalizeColorValue(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    const normalized = value.trim();
    if (!normalized) {
      return '';
    }

    if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
      return normalized.toLowerCase();
    }

    if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
      return `#${normalized.toLowerCase()}`;
    }

    if (/^[0-9a-fA-F]{6}#$/.test(normalized)) {
      return `#${normalized.slice(0, 6).toLowerCase()}`;
    }

    const rgbMatch = normalized.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      const [r, g, b] = rgbMatch.slice(1, 4).map((v) => {
        const parsed = Number(v);
        return Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(255, parsed));
      });
      return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
    }

    return normalized.toLowerCase();
  }

  private syncItemColorsToPalette(): void {
    const itemsArray = this.status();
    for (let i = 0; i < itemsArray.length; i++) {
      const item = itemsArray.at(i);
      const colorControl = item.get('color');
      const colorNameControl = item.get('color_name');
      if (!colorControl) continue;

      const rawValue = colorControl.value;
      const idKey = rawValue !== null && rawValue !== undefined ? String(rawValue).trim() : '';

      if (idKey && this.colorValueById[idKey]) {
        colorControl.setValue(this.colorValueById[idKey], { emitEvent: false });
        if (colorNameControl && !colorNameControl.value) {
          colorNameControl.setValue(this.colorNameById[idKey] || '', { emitEvent: false });
        }
      }

      if (typeof colorControl.value === 'string' && this.colorHexByName[colorControl.value]) {
        colorControl.setValue(this.colorHexByName[colorControl.value], { emitEvent: false });
      }

      const normalized = this.normalizeColorValue(colorControl.value);
      if (normalized !== colorControl.value) {
        colorControl.setValue(normalized, { emitEvent: false });
      }

      const paletteColorName = this.colorNameByValue[normalized];
      if (paletteColorName && colorNameControl?.value !== paletteColorName) {
        colorNameControl?.setValue(paletteColorName, { emitEvent: false });
      }

      if (normalized && !this.colorNameByValue[normalized]) {
        const storedName = colorNameControl?.value;
        const paletteHex = storedName ? this.colorHexByName[storedName] : null;
        if (paletteHex) {
          colorControl.setValue(paletteHex, { emitEvent: false });
          const remappedName = this.colorNameByValue[paletteHex];
          if (remappedName) {
            colorNameControl?.setValue(remappedName, { emitEvent: false });
          }
        }
      }

      const finalColor = this.normalizeColorValue(colorControl.value);
      if (finalColor && !this.colorNameByValue[finalColor]) {
        colorControl.setValue('', { emitEvent: false });
        colorNameControl?.setValue('', { emitEvent: false });
      }
    }
  }

  status(): FormArray {
    return this.completionStatusForm.get('items') as FormArray;
  }

  addItem(choice?: any): void {
    this.status().push(this.createItem(choice));
  }

  deleteItem(index: number) {
    this.status().removeAt(index);
  }

  save(): void {
    const items = this.completionStatusForm.get('items')?.value;

    if (this.isEditMode) {
      const choices = items.map((item: any) => ({ id: item.id, color: item.color }));
      this.dynamicService.updateCompletionStatusColors(this.data.id, choices).subscribe({
        next: () => this._dialogRef.close({ id: this.data.id }),
        error: () => {},
      });
    } else {
      this._dialogRef.close({
        id: this.data.id,
        name: this.data.textValue,
        choices: items,
      });
    }
  }


  canSave(): boolean {
    if (!this.completionStatusForm) {
      return false;
    }

    const nameIsValid = this.isEditMode || this.isFilled(this.data?.textValue);
    const itemsArray = this.status();
    if (!itemsArray || itemsArray.length === 0) {
      return false;
    }

    const itemsAreValid = itemsArray.controls.every((item) => {
      const name = item.get('name')?.value;
      const valueControl = item.get('value');
      const color = item.get('color')?.value;
      const valueIsValid = this.isEditMode || valueControl?.valid;
      return this.isFilled(name) && this.isFilled(valueControl?.value) && valueIsValid && this.isFilled(color);
    });

    return nameIsValid && itemsAreValid;
  }

  private isFilled(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return value !== null && value !== undefined;
  }

  getSelectedColor(index: number): string {
    return this.status().at(index).get('color')?.value || '';
  }

  getColorWrapperWidth(index: number): string {
    const selectedColorValue = this.status().at(index)?.get('color')?.value;
    const selectedLabel = this.getColorDisplayName(selectedColorValue) || 'اسم اللون';
    const labelLength = selectedLabel.trim().length;

    // Keep dynamic sizing while preventing layout shift on adjacent fields.
    const clampedChars = Math.max(4, Math.min(16, labelLength));
    return `clamp(7rem, calc(${clampedChars}ch + 3.75rem), 10.5rem)`;
  }
}
